/**
 * Table.
 *
 * A real table element, because this product's tables are tables: a screen
 * reader user gets row and column headers for free, and so does anyone who
 * copies a block of cells out into a spreadsheet.
 *
 * Two rules the product depends on and this component enforces:
 *   - the header sticks, so a scrolled list never loses what its columns mean;
 *   - a numeric column is mono, tabular and right aligned, so digits line up
 *     into the machine spine every dense surface in this product carries.
 *
 * Mono means copyable literal and nothing else. A column of words never gets
 * the mono flag, however much it would tidy the alignment.
 */

import type { ReactNode } from 'react';
import '../../styles/components/table.css';

export interface TableColumn<Row> {
  key: string;
  header: string;
  /** end is the numeric alignment. It implies nothing on its own: pass mono. */
  align?: 'start' | 'end';
  mono?: boolean;
  width?: string;
  render: (row: Row) => ReactNode;
}

export interface TableProps<Row> {
  /** Names the table for a screen reader. Not painted. */
  caption: string;
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Turns the header sticky by bounding the scroll container. */
  maxHeight?: string;
  empty?: ReactNode;
  /**
   * Below a phone width, stack each row into labelled lines instead of
   * scrolling sideways. Opt-in, because for most tables here a sideways scroll
   * is right: a ledger is read by scanning a column, and stacking destroys
   * that. Turn it on where every cell of one row has to be ACTED on, since a
   * column hidden off the right edge of a phone may as well not exist.
   */
  stackOnNarrow?: boolean;
}

export function Table<Row>({
  caption,
  columns,
  rows,
  rowKey,
  maxHeight,
  empty,
  stackOnNarrow = false,
}: TableProps<Row>) {
  if (rows.length === 0 && empty) {
    return <div className="table__empty">{empty}</div>;
  }

  return (
    <div
      className="table"
      data-stack={stackOnNarrow ? 'yes' : 'no'}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="table__el">
        <caption className="table__caption">{caption}</caption>
        <thead className="table__head">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className="table__th"
                data-align={column.align ?? 'start'}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className="table__row" key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className="table__td"
                  data-align={column.align ?? 'start'}
                  data-mono={column.mono === true ? 'yes' : 'no'}
                  // Carried always, painted only by the stacked layout, where
                  // it replaces the column header a stacked row cannot show.
                  data-label={column.header}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
